import { CheckCircle, Circle, Clock, ShieldOff, XCircle } from 'lucide-react';
import * as React from 'react';

import { Badge } from '@/components/ui/catalyst/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import type {
  RuleEvaluationSummarySchema,
  SlaStatus,
} from '@/services/api/generated/externalTaskService';

interface RuleMatchCellProps {
  evaluations: RuleEvaluationSummarySchema[] | undefined;
  isExcluded?: boolean;
  excludedByRuleName?: string | null;
  isExclusionPending?: boolean;
  pendingExclusionRuleName?: string | null;
}

/**
 * Table cell component showing whether any rules have matched the task.
 * Shows exclusion status if the task is excluded.
 */
export const RuleMatchCell: React.FC<RuleMatchCellProps> = ({
  evaluations,
  isExcluded,
  excludedByRuleName,
  isExclusionPending,
  pendingExclusionRuleName,
}) => {
  // Show excluded status if task is excluded
  if (isExcluded) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger className="flex items-center gap-1">
            <ShieldOff className="h-3.5 w-3.5 text-orange-500" />
            <span className="text-xs text-orange-600 font-medium">
              Excluded
            </span>
          </TooltipTrigger>
          <TooltipContent>
            <p>Excluded from automatic linking</p>
            {excludedByRuleName && <p>By rule: {excludedByRuleName}</p>}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  if (isExclusionPending) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger className="flex items-center gap-1">
            <Clock className="h-3.5 w-3.5 text-amber-500" />
            <span className="text-xs text-amber-600 font-medium">
              Updating
            </span>
          </TooltipTrigger>
          <TooltipContent>
            <p>Automatic linking status updating</p>
            <p>Rule change is being reconciled</p>
            {pendingExclusionRuleName && <p>Rule: {pendingExclusionRuleName}</p>}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  if (!evaluations || evaluations.length === 0) {
    return <span className="text-xs text-gray-400">-</span>;
  }

  const matchedCount = evaluations.filter(
    (e) => e.evaluation_status === 'MATCHED',
  ).length;

  if (matchedCount === 0) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger className="flex items-center gap-1">
            <Circle className="h-3.5 w-3.5 text-gray-400" />
            <span className="text-xs text-gray-500">No match</span>
          </TooltipTrigger>
          <TooltipContent>
            <p>Evaluated by {evaluations.length} rule(s), none matched</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger className="flex items-center gap-1">
          <CheckCircle className="h-3.5 w-3.5 text-green-600" />
          <span className="text-xs text-green-700 font-medium">
            {matchedCount} matched
          </span>
        </TooltipTrigger>
        <TooltipContent>
          <div className="space-y-1">
            {evaluations
              .filter((e) => e.evaluation_status === 'MATCHED')
              .map((e) => (
                <p key={e.rule_id}>
                  {e.rule_name}: {e.links_created} link
                  {e.links_created !== 1 ? 's' : ''}
                </p>
              ))}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

interface SlaStatusCellProps {
  evaluations: RuleEvaluationSummarySchema[] | undefined;
  isExcluded?: boolean;
  isExclusionPending?: boolean;
}

/**
 * Table cell component showing the SLA status for matched rules.
 * Excluded tasks always show as "On time" since they don't need linking.
 */
export const SlaStatusCell: React.FC<SlaStatusCellProps> = ({
  evaluations,
  isExcluded,
  isExclusionPending,
}) => {
  // Excluded tasks are always "On time" - they don't need linking
  if (isExcluded) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger>
            <Badge color="green" className="flex items-center gap-1">
              <CheckCircle className="h-3.5 w-3.5 text-emerald-600" />
              <span className="text-xs">On time</span>
            </Badge>
          </TooltipTrigger>
          <TooltipContent>
            <p>Excluded tasks are always considered on time</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  if (isExclusionPending) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger>
            <Badge color="yellow" className="flex items-center gap-1">
              <Clock className="h-3.5 w-3.5 text-amber-600" />
              <span className="text-xs">Pending</span>
            </Badge>
          </TooltipTrigger>
          <TooltipContent>
            <p>Automatic linking status is updating</p>
            <p>Rule reconciliation is still running</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  if (!evaluations || evaluations.length === 0) {
    return <span className="text-xs text-gray-400">-</span>;
  }

  // Get the "worst" SLA status: late > pending > on_time > no_sla
  const slaStatuses = evaluations
    .filter((e) => e.evaluation_status === 'MATCHED' && e.sla_status)
    .map((e) => e.sla_status);

  if (slaStatuses.length === 0) {
    return <span className="text-xs text-gray-400">-</span>;
  }

  const getWorstStatus = (
    statuses: (SlaStatus | null | undefined)[],
  ): SlaStatus => {
    if (statuses.includes('late')) return 'late';
    if (statuses.includes('pending')) return 'pending';
    if (statuses.includes('on_time')) return 'on_time';
    return 'no_sla';
  };

  const worstStatus = getWorstStatus(slaStatuses);

  const getStatusDisplay = (status: SlaStatus) => {
    switch (status) {
      case 'on_time':
        return {
          icon: <CheckCircle className="h-3.5 w-3.5 text-emerald-600" />,
          label: 'On time',
          color: 'green' as const,
        };
      case 'late':
        return {
          icon: <XCircle className="h-3.5 w-3.5 text-red-600" />,
          label: 'Late',
          color: 'red' as const,
        };
      case 'pending':
        return {
          icon: <Clock className="h-3.5 w-3.5 text-amber-600" />,
          label: 'Pending',
          color: 'yellow' as const,
        };
      case 'no_sla':
        return {
          icon: <Circle className="h-3.5 w-3.5 text-gray-400" />,
          label: 'No SLA',
          color: 'zinc' as const,
        };
      default:
        return null;
    }
  };

  const display = getStatusDisplay(worstStatus);
  if (!display) return <span className="text-xs text-gray-400">-</span>;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger>
          <Badge color={display.color} className="flex items-center gap-1">
            {display.icon}
            <span className="text-xs">{display.label}</span>
          </Badge>
        </TooltipTrigger>
        <TooltipContent>
          <div className="space-y-1">
            {evaluations
              .filter((e) => e.evaluation_status === 'MATCHED' && e.sla_status)
              .map((e) => {
                const statusInfo = getStatusDisplay(e.sla_status!);
                return (
                  <div key={e.rule_id} className="flex items-center gap-2">
                    <span>{e.rule_name}:</span>
                    <span
                      className={
                        statusInfo?.color === 'red'
                          ? 'text-red-600'
                          : statusInfo?.color === 'green'
                            ? 'text-emerald-600'
                            : 'text-amber-600'
                      }
                    >
                      {statusInfo?.label}
                    </span>
                  </div>
                );
              })}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
